import React from 'react';
import { useNavigate } from 'react-router-dom';
import { isNativeApp } from '../../utils/platform';

/**
 * TravelTimeInfo - Displays travel times between rides
 * Shows time and distance from previous ride and to next ride
 * Includes tier-based access control (tiers 1-4 only)
 */
const TravelTimeInfo = ({ travelTimes, loading, error, isFreeTeir }) => {
  const navigate = useNavigate();
  if (loading) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
        <div className="flex items-center gap-2">
          <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
          <span className="text-sm text-blue-800">Calculating travel times...</span>
        </div>
      </div>
    );
  }

  // Free tier users see upgrade prompt
  if (isFreeTeir) {
    return (
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4 mt-4">
        <div className="flex items-center gap-3">
          <div className="text-2xl">✨</div>
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-purple-900">
              Upgrade to See Travel Times
            </h4>
            <p className="text-xs text-purple-700 mt-1">
              Premium drivers get real-time travel time calculations between rides to help plan their schedule better.
            </p>
          </div>
          {!isNativeApp() && (
            <button
              onClick={() => navigate('/driver/subscription')}
              className="bg-purple-600 text-white text-xs px-3 py-2 rounded hover:bg-purple-700 transition-colors"
            >
              Upgrade
            </button>
          )}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
        <div className="flex items-center gap-2">
          <span className="text-red-600">⚠️</span>
          <span className="text-sm text-red-800">{error}</span>
        </div>
      </div>
    );
  }

  if (!travelTimes) {
    return null;
  }

  const { before, after } = travelTimes;
  const hasNearbyRides = before || after;

  if (!hasNearbyRides) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-4">
        <div className="text-center text-sm text-gray-600">
          <span className="text-lg mb-1 block">🚗</span>
          No scheduled rides within 6 hours before or after this request.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-300 rounded-lg mt-4 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 px-4 py-2">
        <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
          <span>🗺️</span>
          Travel Time Between Rides
        </h4>
      </div>

      <div className="divide-y divide-gray-200">
        {/* Before ride */}
        {before ? (
          <div className="p-4">
            <div className="flex items-start gap-3">
              <div className="bg-blue-100 text-blue-700 rounded-full p-2 mt-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </div>
              <div className="flex-1">
                <div className="text-xs text-gray-500 mb-1">From Previous Ride</div>
                {before.error ? (
                  <div className="text-xs text-red-600">{before.error}</div>
                ) : (
                  <>
                    <div className="text-sm font-semibold text-gray-900">
                      🚗 {before.duration_minutes} min ({before.distance_miles} mi) to get to this ride
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      Time to drive from your previous ride's dropoff to this ride's pickup
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="p-4 bg-gray-50">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span>←</span>
              <span>No rides before</span>
            </div>
          </div>
        )}

        {/* After ride */}
        {after ? (
          <div className="p-4">
            <div className="flex items-start gap-3">
              <div className="bg-green-100 text-green-700 rounded-full p-2 mt-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <div className="flex-1">
                <div className="text-xs text-gray-500 mb-1">To Next Ride</div>
                {after.error ? (
                  <div className="text-xs text-red-600">{after.error}</div>
                ) : (
                  <>
                    <div className="text-sm font-semibold text-gray-900">
                      🚗 {after.duration_minutes} min ({after.distance_miles} mi) to next ride
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      Time to drive from this ride's dropoff to your next ride's pickup
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="p-4 bg-gray-50">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span>→</span>
              <span>No rides after</span>
            </div>
          </div>
        )}
      </div>

      {/* Footer note */}
      {hasNearbyRides && (before || after) && !before?.error && !after?.error && (
        <div className="bg-gray-50 border-t border-gray-200 px-4 py-2">
          <p className="text-xs text-gray-600">
            💡 Travel times include current traffic conditions to help you plan realistically.
          </p>
        </div>
      )}
    </div>
  );
};

export default TravelTimeInfo;
